import React, {memo} from 'react';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {getImageDomain, getLinkPath, scrollToTop} from 'app/utils';
import {Link} from 'react-router-dom';
import type ArticleInterface from 'app/interfaces/articles/ArticleInterface';
import {configStore} from 'app/store/global';
import {useCarousel} from 'app/utils/useCarousel';

interface CasinoPromotionsBlockProps {
    promotions: ArticleInterface[];
    style?: React.CSSProperties;
}

const getPromoTitle = (promo: ArticleInterface): string => {
    const promoLangs = promo.promo_title_langs;
    const defaultLocale = configStore.DEFAULT_LOCALE;

    return promoLangs && (promoLangs[I18n.locale] || promoLangs[defaultLocale]) || promo.title || '';
};

const CasinoPromotionsBlock = ({promotions, style}: CasinoPromotionsBlockProps): React.ReactElement | null => {
    const itemsCount = promotions.length;
    const {activeIndex, handleNextClick, handlePrevClick} = useCarousel(itemsCount);

    const currentPromo = promotions[activeIndex];
    const domain = getImageDomain(false);
    const imageUrl = currentPromo?.preview_image ? `${domain}${currentPromo.preview_image}` : null;

    if (itemsCount === 0) {
        return null;
    }

    return (
        <div className="casino-promotions-block big-marketing-block marketing-block" style={style}>
            <div className="casino-promotions-block__header">
                <SpriteIcon
                    name="#svg-speakerphone"
                    className="casino-promotions-block__icon svg-default"
                    size={{height: 20, width: 20}}
                />
                <span className="casino-promotions-block__title">
                    {I18n.t('promotions_title')}
                </span>
            </div>
            <div className="casino-promotions-block__banner-wrap">
                {itemsCount > 1 && <div className="marketing-block-progress">
                    {promotions.map((_, i) => {
                        const isCurrent = i === activeIndex;
                        const isActive = i < activeIndex;
                        const segmentClass = `marketing-block-progress__segment ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`;

                        return <span key={i} className={segmentClass} />;
                    })}
                </div>}
                <Link
                    to={getLinkPath(`/promotions/${currentPromo.slug}`)}
                    className="casino-promotions-block__banner big-marketing-block__banner"
                    onClick={scrollToTop}
                >
                    <img
                        key={activeIndex}
                        src={imageUrl}
                        alt={currentPromo.title}
                        className="casino-promotions-block__banner-img"
                    />
                    <span className="casino-promotions-block__promo-name">
                        {getPromoTitle(currentPromo)}
                    </span>
                </Link>
                {itemsCount > 1 && <>
                    <button
                        type="button"
                        className="marketing-block-arrow marketing-block-arrow_prev"
                        onClick={handlePrevClick}
                    >
                        <SpriteIcon name="#pag-arr-left" className="svg-default"/>
                    </button>
                    <button
                        type="button"
                        className="marketing-block-arrow marketing-block-arrow_next"
                        onClick={handleNextClick}
                    >
                        <SpriteIcon name="#pag-arr-right" className="svg-default"/>
                    </button>
                </>}
            </div>
        </div>
    );
};

export default memo(CasinoPromotionsBlock);
